/**
 * Home Page Component
 * Landing page showing available tools and quick actions
 */
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  makeStyles,
  tokens,
  Text,
  Card,
  CardHeader,
  Button,
  Spinner,
} from "@fluentui/react-components";
import {
  Apps24Regular,
  Apps24Filled,
  ArrowRight16Regular,
  BrainCircuit24Regular,
  BrainCircuit24Filled,
  ClipboardTask24Regular,
  ClipboardTask24Filled,
  People24Regular,
  People24Filled,
  Globe24Regular,
  Globe24Filled,
  bundleIcon,
} from "@fluentui/react-icons";

const AppsIcon = bundleIcon(Apps24Filled, Apps24Regular);
const BrainCircuitIcon = bundleIcon(BrainCircuit24Filled, BrainCircuit24Regular);
const ClipboardTaskIcon = bundleIcon(ClipboardTask24Filled, ClipboardTask24Regular);
const PeopleIcon = bundleIcon(People24Filled, People24Regular);
const GlobeIcon = bundleIcon(Globe24Filled, Globe24Regular);

const toolIconMap: Record<string, React.FC> = {
  "cad-analysis": BrainCircuitIcon,
  "sharepoint-agent": ClipboardTaskIcon,
  "crm": PeopleIcon,
  "client-map": GlobeIcon,
};

const getToolIcon = (toolId: string) => {
  const IconComponent = toolIconMap[toolId];
  if (IconComponent) {
    return <IconComponent />;
  }
  return <AppsIcon />;
};

import { useToolRegistry } from "../tools/useToolRegistry";
import type { Tool } from "../tools/types";

const useStyles = makeStyles({
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalL,
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalL,
    borderBottom: "1px solid #e2e8f0",
  },
  greeting: {
    marginBottom: tokens.spacingVerticalS,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
  },
  toolsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "20px",
  },
  toolCard: {
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    ":hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
    },
  },
  toolIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #004C97 0%, #0066cc 100%)",
    color: "#ffffff",
  },
  toolDescription: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
  toolAction: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalM,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
  },
  emptyState: {
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

// Users with Executive Dashboard access land there instead of the tile grid,
// once per browser session — the Home link in the sidebar still shows the grid.
const EXECUTIVE_LANDING_FLAG = "photon.executiveLandingDone";

export const HomePage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { accessibleTools, isLoading } = useToolRegistry();

  const user = accounts[0];
  const firstName = user?.name?.split(" ")[0] || "there";

  const landOnExecutiveDashboard =
    !isLoading &&
    !sessionStorage.getItem(EXECUTIVE_LANDING_FLAG) &&
    accessibleTools.some((tool) => tool.id === "executive-dashboard");

  React.useEffect(() => {
    if (landOnExecutiveDashboard) {
      sessionStorage.setItem(EXECUTIVE_LANDING_FLAG, "1");
    }
  }, [landOnExecutiveDashboard]);

  if (landOnExecutiveDashboard) {
    return <Navigate to="/tool/executive-dashboard" replace />;
  }

  const handleToolClick = (tool: Tool) => {
    if (tool.type === "external" && tool.externalUrl) {
      window.open(tool.externalUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/tool/${tool.id}`);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Text size={800} weight="semibold" className={styles.greeting} block>
          Welcome back, {firstName}!
        </Text>
        <Text size={400} style={{ color: tokens.colorNeutralForeground3 }}>
          Select a tool below to get started, or use the sidebar to navigate.
        </Text>
      </div>

      {/* Tools Grid */}
      {isLoading ? (
        <div className={styles.loading}>
          <Spinner size="large" label="Loading tools..." />
        </div>
      ) : accessibleTools.length === 0 ? (
        <div className={styles.emptyState}>
          <Text size={500}>No tools available</Text>
          <Text size={400} block style={{ marginTop: "8px" }}>
            Contact your administrator to request access to tools.
          </Text>
        </div>
      ) : (
        <div className={styles.toolsGrid}>
          {accessibleTools.map((tool) => (
            <Card
              key={tool.id}
              className={styles.toolCard}
              onClick={() => handleToolClick(tool)}
            >
              <CardHeader
                image={
                  <div className={styles.toolIcon}>
                    {getToolIcon(tool.id)}
                  </div>
                }
                header={<Text weight="semibold">{tool.name}</Text>}
                description={
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {tool.category || "General"}
                  </Text>
                }
              />
              <Text className={styles.toolDescription}>{tool.description}</Text>
              <div className={styles.toolAction}>
                <Button appearance="transparent" size="small">
                  Open tool
                  <ArrowRight16Regular />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomePage;
